import { Loadable } from '@/components/loaders';
import { useWorkspace } from '@/features/workspaces/hooks';
import { StepPageActions } from '@/global/components';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { Select } from '@/lib/Selects';
import { BaseAudience } from '@/models/audiences';
import { ISelectItem } from '@/models/select';
import { useGetAudiencesQuery } from '@/services/endpoints/audiences';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type CustomConnectorAudienceSelectionStepProps = {
  onNext: (audience: BaseAudience) => void;
  onCancel: () => void;
};

export const CustomConnectorAudienceSelectionStep: FC<CustomConnectorAudienceSelectionStepProps> = ({ onNext, onCancel }) => {
  const { id: workspaceId } = useWorkspace();
  const { t } = useTranslation();
  const { data: audiences, isLoading } = useGetAudiencesQuery({ workspaceId });

  const [selectedAudience, setSelectedAudience] = useState<BaseAudience | null>(null);

  const audienceSelectItems: ISelectItem[] = useMemo(() => {
    return audiences?.items?.map((audience) => ({
      id: audience.id,
      label: audience.name,
      description: `Schedule: ${audience.scheduleType}`,
    })) || [];
  }, [audiences]);

  const handleNext = () => {
    if (!selectedAudience) return;
    onNext(selectedAudience);
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON.cancel',
    onClick: onCancel,
  };

  const nextButton: StepPageActionsButton = {
    text: 'ALL:next',
    onClick: handleNext,
    disabled: !selectedAudience,
  };

  return (
    <div className='flex grow flex-col gap-6 p-6'>
      <div className="flex flex-col border-b border-secondary-border pb-8">
        <h2 className="text-lg font-semibold leading-7 text-primary-900">{t('ALL:destinations.customConnectorAudienceSelection.title')}</h2>
        <h4 className="text-sm leading-5 text-tertiary-600">{t('ALL:destinations.customConnectorAudienceSelection.subtitle')}</h4>
      </div>

      <Loadable isLoading={isLoading}>
        <div className='space-y-6'>
          <div>
            <label className='mb-2 block text-sm font-medium text-gray-700'>{t('ALL:destinations.customConnectorAudienceSelection.available_audiences_label')}</label>
            <Select
              items={audienceSelectItems}
              value={audienceSelectItems.find((item) => item.id === selectedAudience?.id)}
                    onChange={(value) => {
                      const audience = audiences?.items?.find((a) => a.id === value.id);
                      setSelectedAudience(audience || null);
                    }}
              placeholder={t('ALL:destinations.customConnectorAudienceSelection.select_audience_placeholder')}
            />
          </div>

          {selectedAudience && (
            <div className='rounded-lg bg-blue-50 p-4'>
              <h4 className='mb-2 font-medium text-blue-900'>{t('ALL:destinations.customConnectorAudienceSelection.selected_audience_title')}</h4>
                 <div className='text-sm text-blue-800'>
                   <p><strong>{t('ALL:destinations.customConnectorAudienceSelection.name_label')}</strong> {selectedAudience.name}</p>
                   <p><strong>{t('ALL:destinations.customConnectorAudienceSelection.schedule_type_label')}</strong> {selectedAudience.scheduleType}</p>
                 </div>
            </div>
          )}
        </div>
      </Loadable>

      <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />
    </div>
  );
};
